import { JsonSerializable } from '@/util/object/JsonSerializable'

/**
 * Stateless Session Storage Service
 */
export class S4 {
  /**
   * Gets window.sessionStorage object
   * @returns {Storage}
   */
  static get sessionStorage () {
    if (window && window.sessionStorage) {
      return window.sessionStorage
    }
    throw new Error('window.sessionStorage is undefined')
  }

  /**
   * Wrapper around window.sessionStorage.getItem(key) to transform
   * sessionStorage response into a JSON object using JSON.parse
   * @param {string} key
   * @returns {Object|null}
   */
  static getItem (key) {
    if (typeof key === 'string') {
      const item = this.sessionStorage.getItem(key)
      if (typeof item === 'string') {
        return JSON.parse(item)
      } else {
        console.debug(`Key ${key} does not exist`)
        return null
      }
    }
    throw new Error('key is undefined or is not a string')
  }

  /**
   * Wrapper around S4.getItem(key) to transform
   * sessionStorage response into a Typed object using Object.assign
   * @template T
   * @param {string} key
   * @param {typeof JsonSerializable} TypeClass
   * @returns {T} Object of TypeClass
   */
  static getObject (key, TypeClass) {
    if (TypeClass instanceof Function && TypeClass.prototype instanceof Object && JsonSerializable.isPrototypeOf(TypeClass)) {
      const item = this.getItem(key)
      if (item !== null) {
        return TypeClass.fromJSON(item)
      } else {
        return null
      }
    }
    throw new Error('TypeClass is not defined : ' + TypeClass)
  }

  /**
   * Wrapper around S4.getItem(key) to transform
   * sessionStorage response into a Typed object using Object.assign
   * @template T
   * @param {string} key
   * @param {typeof JsonSerializable} TypeClass
   * @returns {Array<T>} Array of TypeClass
   */
  static getArray (key, TypeClass) {
    if (TypeClass instanceof Function && TypeClass.prototype instanceof Object && JsonSerializable.isPrototypeOf(TypeClass)) {
      const array = this.getItem(key)
      if (array !== null) {
        return array.map(item => TypeClass.fromJSON(item))
      } else {
        return null
      }
    }
    throw new Error('TypeClass is not defined : ' + TypeClass)
  }

  /**
   * Wrapper around window.sessionStorage.setItem(key, itemStr) to transform
   * item response into a string using JSON.stringify
   * @param {string} key
   * @param {Object} item
   */
  static setItem (key, item) {
    if (key && item instanceof Object) {
      this.sessionStorage.setItem(key, JSON.stringify(item))
    } else if (!key) {
      throw new Error('Required storage key is not defined')
    } else if (!item) {
      throw new Error('Required item data is not defined')
    } else if (typeof item === 'object') {
      throw new Error('Required item data is not an object')
    }
  }

  /**
   * Wrapper around window.sessionStorage.setItem(key, itemStr) to transform
   * item response into a string using JSON.stringify
   * @param {string} key
   * @param {typeof JsonSerializable} item
   */
  static setObject (key, item) {
    if (item instanceof JsonSerializable) {
      return this.setItem(key, item.toJSON())
    }
    throw new Error('item is not defined : ' + item)
  }

  /**
   * Wrapper around window.sessionStorage.setItem(key, itemStr) to transform
   * item response into a string using JSON.stringify
   * @param {string} key
   * @param {Array<typeof JsonSerializable>} items
   */
  static setArray (key, items) {
    if (Array.isArray(items)) {
      if (items.every(item => item instanceof JsonSerializable)) {
        return this.setItem(key, items.map(item => item.toJSON()))
      }
      throw new Error('item in array is not JsonSerializable : ' + items.filter(item => !(item instanceof JsonSerializable)))
    }
    throw new Error('items is not an array : ' + items)
  }

  /**
   * Wrapper around window.sessionStorage.removeItem(key)
   * @param {string} key
   */
  static removeItem (key) {
    this.sessionStorage.removeItem(key)
  }
}
